import { apiClient } from './apiClient';

export interface ChannelScrapingServer {
  id: string;
  name: string;
  url: string;
  username?: string | null;
  enabled?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ChannelScrapingWorkerServer {
  id: string;
  name: string;
  baseUrl: string;
  enabled?: boolean | null;
  notes?: string | null;
  readOnly?: boolean | null;
  createdDate?: string | null;
  updatedDate?: string | null;
}

export interface ChannelScrapingTask {
  id: string;
  scrapingServerId?: string | null;
  scrapingServerName?: string | null;
  url: string;
  stopPatternRequest?: string | null;
  blockAds?: boolean | null;
  serverId?: string | null;
  streamName?: string | null;
  frequencyHours?: number | null;
  userAgent?: string | null;
  httpHeaders?: Record<string, string> | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastRunAt?: string | null;
  lastStatus?: string | null;
  lastMatchedUrl?: string | null;
  lastDeliveryMode?: 'direct' | 'ffmpeg' | null;
  lastVideoUrl?: string | null;
  lastAudioUrl?: string | null;
  lastPublisherOutputUrl?: string | null;
  lastError?: string | null;
  nextRunAt?: string | null;
}

export interface ChannelScrapingServerPayload {
  name: string;
  url: string;
  username: string;
  password?: string;
}

export interface ChannelScrapingWorkerServerPayload {
  name: string;
  baseUrl: string;
  enabled: boolean;
  notes?: string;
}

export interface ChannelScrapingTaskPayload {
  url: string;
  stopPatternRequest: string;
  blockAds: boolean;
  server: string;
  scrapingServerIds?: string[];
  streamName: string;
  frequency: number;
  userAgent?: string;
  httpHeaders?: Record<string, string>;
}

export interface ChannelScrapingTaskRunSummary {
  runId: string;
  taskId: string;
  status?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  matchedUrl?: string | null;
  deliveryMode?: 'direct' | 'ffmpeg' | null;
  videoUrl?: string | null;
  audioUrl?: string | null;
  publisherOutputUrl?: string | null;
  serverStatus?: string | null;
  serverResponse?: unknown;
  error?: string | null;
}

type RunStreamHandlers = {
  onRun?: (line: string) => void;
  onLog?: (line: string) => void;
  onComplete?: (line: string) => void;
  onError?: (error: Error) => void;
};

const getApiBaseUrl = () => apiClient.getInstance().defaults.baseURL?.replace(/\/+$/, '') ?? '';

const withScrapingServer = (path: string, scrapingServerId?: string | null) => {
  if (!scrapingServerId) {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}scrapingServerId=${encodeURIComponent(scrapingServerId)}`;
};

export const channelScrapingService = {
  async getServers(scrapingServerId?: string | null): Promise<{ success: boolean; servers: ChannelScrapingServer[] }> {
    return apiClient.get(withScrapingServer('/channel-scraping/servers', scrapingServerId));
  },

  async createServer(request: ChannelScrapingServerPayload, scrapingServerId?: string | null): Promise<{ success: boolean; server: ChannelScrapingServer }> {
    return apiClient.post(withScrapingServer('/channel-scraping/servers', scrapingServerId), request);
  },

  async updateServer(id: string, request: ChannelScrapingServerPayload, scrapingServerId?: string | null): Promise<{ success: boolean; server: ChannelScrapingServer }> {
    return apiClient.put(withScrapingServer(`/channel-scraping/servers/${id}`, scrapingServerId), request);
  },

  async deleteServer(id: string, scrapingServerId?: string | null): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(withScrapingServer(`/channel-scraping/servers/${id}`, scrapingServerId));
  },

  async getScrapingServers(): Promise<{ success: boolean; servers: ChannelScrapingWorkerServer[] }> {
    return apiClient.get('/channel-scraping/scraping-servers');
  },

  async createScrapingServer(request: ChannelScrapingWorkerServerPayload): Promise<{ success: boolean; server: ChannelScrapingWorkerServer }> {
    return apiClient.post('/channel-scraping/scraping-servers', request);
  },

  async updateScrapingServer(id: string, request: ChannelScrapingWorkerServerPayload): Promise<{ success: boolean; server: ChannelScrapingWorkerServer }> {
    return apiClient.put(`/channel-scraping/scraping-servers/${id}`, request);
  },

  async deleteScrapingServer(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/channel-scraping/scraping-servers/${id}`);
  },

  async getTasks(scrapingServerId?: string | null): Promise<{ success: boolean; tasks: ChannelScrapingTask[] }> {
    return apiClient.get(withScrapingServer('/channel-scraping/tasks', scrapingServerId));
  },

  async getTask(id: string, scrapingServerId?: string | null): Promise<{ success: boolean; task: ChannelScrapingTask }> {
    return apiClient.get(withScrapingServer(`/channel-scraping/tasks/${id}`, scrapingServerId));
  },

  async createTask(request: ChannelScrapingTaskPayload): Promise<{ success: boolean; task?: ChannelScrapingTask; tasks?: ChannelScrapingTask[] }> {
    return apiClient.post('/channel-scraping/tasks', request);
  },

  async updateTask(id: string, request: ChannelScrapingTaskPayload, scrapingServerId?: string | null): Promise<{ success: boolean; task: ChannelScrapingTask }> {
    return apiClient.put(withScrapingServer(`/channel-scraping/tasks/${id}`, scrapingServerId), request);
  },

  async deleteTask(id: string, scrapingServerId?: string | null): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(withScrapingServer(`/channel-scraping/tasks/${id}`, scrapingServerId));
  },

  async runTask(id: string, scrapingServerId?: string | null): Promise<ChannelScrapingTaskRunSummary> {
    return apiClient.post(withScrapingServer(`/channel-scraping/tasks/${id}/run`, scrapingServerId));
  },

  async getTaskRun(runId: string, scrapingServerId?: string | null): Promise<ChannelScrapingTaskRunSummary> {
    return apiClient.get(withScrapingServer(`/channel-scraping/task-runs/${runId}`, scrapingServerId));
  },

  async getTaskRunLogs(runId: string, scrapingServerId?: string | null): Promise<string> {
    const response = await fetch(`${getApiBaseUrl()}${withScrapingServer(`/channel-scraping/task-runs/${runId}/logs`, scrapingServerId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'text/plain',
        'Accept-Language': localStorage.getItem('language') || 'es',
      },
    });

    if (!response.ok) {
      throw new Error('No se pudieron cargar los logs');
    }

    return response.text();
  },

  streamTaskRunLogs(runId: string, scrapingServerId: string | null | undefined, handlers: RunStreamHandlers): () => void {
    const controller = new AbortController();
    let completed = false;

    void (async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}${withScrapingServer(`/channel-scraping/task-runs/${runId}/logs/stream`, scrapingServerId)}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
            'Accept-Language': localStorage.getItem('language') || 'es',
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('No se pudo conectar al stream de logs');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const dispatchEvent = (chunk: string) => {
          const lines = chunk.split('\n');
          let eventName = 'message';
          const dataLines: string[] = [];

          for (const rawLine of lines) {
            const line = rawLine.trimEnd();
            if (!line || line.startsWith(':')) {
              continue;
            }
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
              continue;
            }
            if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          }

          if (!dataLines.length) {
            return;
          }

          const rawData = dataLines.join('\n');

          if (eventName === 'run') {
            handlers.onRun?.(rawData);
            return;
          }

          if (eventName === 'log') {
            handlers.onLog?.(rawData);
            return;
          }

          if (eventName === 'complete') {
            completed = true;
            handlers.onComplete?.(rawData);
            controller.abort();
          }
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';

          for (const eventChunk of events) {
            dispatchEvent(eventChunk);
          }
        }

        if (buffer.trim()) {
          dispatchEvent(buffer);
        }
      } catch (error) {
        if (controller.signal.aborted || completed) {
          return;
        }
        handlers.onError?.(error instanceof Error ? error : new Error('No se pudo leer el stream de logs'));
      }
    })();

    return () => controller.abort();
  },
};
